import { z } from "zod";
import { dateOnlySchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { blankToNull, timestampsShape } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * CONTRACTS & E-SIGNATURE (PRD §8 Item 8, US-8.3 and US-8.6).
 *
 * An electronic signature is recognised under India's IT Act, 2000, and what
 * makes it stand up is the EVIDENCE, not the picture: who signed, from which
 * address, with which browser, at which instant, and — the part that matters —
 * a SHA-256 hash of the exact document bytes they were shown. Aadhaar eSign and
 * e-stamping are Phase 3; everything here is the evidence trail that Phase 3
 * will hang off.
 *
 * Two rules the schemas encode:
 *  · A contract hangs off a CLIENT (`clientId` is required). A proposal may sit
 *    on a lead; a contract never does — the lead must convert first (PRD I-2).
 *  · The body is FROZEN at send. `bodyHash` is written at the same moment and
 *    re-checked on every download, so "signed documents are immutable" is a
 *    verifiable claim rather than a promise.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const CONTRACT_STATUSES = ["draft", "sent", "viewed", "signed", "countersigned", "active", "expired", "terminated"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
export const contractStatusSchema = z.enum(CONTRACT_STATUSES);

/** Only a draft may be edited; from `sent` onwards the body is the record. */
export const CONTRACT_EDITABLE_STATUSES: readonly ContractStatus[] = ["draft"];

export const CONTRACT_KINDS = ["msa", "sow", "nda", "amendment", "other"] as const;
export type ContractKind = (typeof CONTRACT_KINDS)[number];
export const contractKindSchema = z.enum(CONTRACT_KINDS);

export const CONTRACT_KIND_LABELS: Record<ContractKind, string> = {
  msa: "Master services agreement",
  sow: "Statement of work",
  nda: "Non-disclosure agreement",
  amendment: "Amendment / change order",
  other: "Agreement",
};

export const SIGNATURE_PARTIES = ["client", "agency"] as const;
export type SignatureParty = (typeof SIGNATURE_PARTIES)[number];
export const signaturePartySchema = z.enum(SIGNATURE_PARTIES);

export const SIGNATURE_METHODS = ["typed", "drawn", "uploaded"] as const;
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];
export const signatureMethodSchema = z.enum(SIGNATURE_METHODS);

export const CONTRACT_EVENT_TYPES = ["sent", "viewed", "signed", "countersigned", "downloaded", "terminated", "renewal_reminded"] as const;
export type ContractEventType = (typeof CONTRACT_EVENT_TYPES)[number];
export const contractEventTypeSchema = z.enum(CONTRACT_EVENT_TYPES);

/** Renewal reminders go out this many days before `effectiveTo` (US-8.6). */
export const CONTRACT_RENEWAL_REMINDER_DAYS = [60, 30, 15] as const;
/** How long a signature link stays usable. */
export const CONTRACT_LINK_DEFAULT_DAYS = 45;

/**
 * Merge fields a contract body may use. Anything not in this list survives the
 * render untouched, so a stray `{{…}}` is visible rather than silently blank.
 */
export const CONTRACT_MERGE_FIELDS = [
  "agency.name",
  "agency.legal_name",
  "agency.gstin",
  "agency.pan",
  "agency.address",
  "agency.email",
  "client.name",
  "client.legal_name",
  "client.number",
  "client.gstin",
  "client.address",
  "client.state",
  "contact.name",
  "contact.email",
  "engagement.name",
  "engagement.fee",
  "engagement.type",
  "engagement.billing",
  "contract.number",
  "contract.title",
  "contract.effective_from",
  "contract.effective_to",
  "contract.value",
  "contract.notice_days",
  "today",
] as const;
export type ContractMergeField = (typeof CONTRACT_MERGE_FIELDS)[number];

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export const contractTemplateVersionSchema = z.object({
  id: uuidSchema,
  templateId: uuidSchema,
  version: z.number().int(),
  body: z.string(),
  createdByUserId: uuidSchema.nullable(),
  createdAt: timestampSchema,
});
export type ContractTemplateVersionDto = z.infer<typeof contractTemplateVersionSchema>;

export const contractTemplateSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  kind: contractKindSchema,
  body: z.string(),
  version: z.number().int(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  /** Countersign the moment the client signs, instead of waiting for a human (US-8.3). */
  autoCountersign: z.boolean(),
  termMonths: z.number().int().nullable(),
  autoRenew: z.boolean(),
  noticeDays: z.number().int(),
  renewalReminderDays: z.array(z.number().int()),
  /** Merge fields found in the body that this workspace cannot fill yet. */
  unknownMergeFields: z.array(z.string()),
  tags: z.array(z.string()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ContractTemplateDto = z.infer<typeof contractTemplateSchema>;

const contractTemplateWritableShape = {
  name: z.string().trim().min(1).max(200),
  description: blankToNull(z.string().max(2000)),
  kind: contractKindSchema,
  body: z.string().max(200_000),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  autoCountersign: z.boolean(),
  termMonths: z.number().int().min(1).max(120).nullable(),
  autoRenew: z.boolean(),
  noticeDays: z.number().int().min(0).max(365),
  renewalReminderDays: z.array(z.number().int().min(1).max(365)).max(6),
  tags: tagsSchema,
};

/** POST /api/v1/contract-templates (perm `contracts:write`) → 201 ContractTemplate. Duplicate name → CONFLICT. */
export const createContractTemplateInputSchema = z
  .object({
    ...contractTemplateWritableShape,
    description: contractTemplateWritableShape.description.optional(),
    kind: contractTemplateWritableShape.kind.optional(),
    isDefault: contractTemplateWritableShape.isDefault.optional(),
    isActive: contractTemplateWritableShape.isActive.optional(),
    autoCountersign: contractTemplateWritableShape.autoCountersign.optional(),
    termMonths: contractTemplateWritableShape.termMonths.optional(),
    autoRenew: contractTemplateWritableShape.autoRenew.optional(),
    noticeDays: contractTemplateWritableShape.noticeDays.optional(),
    renewalReminderDays: contractTemplateWritableShape.renewalReminderDays.optional(),
    tags: contractTemplateWritableShape.tags.optional(),
  })
  .strict();
export type CreateContractTemplateInput = z.input<typeof createContractTemplateInputSchema>;
export type CreateContractTemplateData = z.output<typeof createContractTemplateInputSchema>;

/** PATCH /api/v1/contract-templates/:id (perm `contracts:write`) → ContractTemplate. A new `body` bumps the version. */
export const updateContractTemplateInputSchema = z.object(contractTemplateWritableShape).partial().strict();
export type UpdateContractTemplateInput = z.input<typeof updateContractTemplateInputSchema>;
export type UpdateContractTemplateData = z.output<typeof updateContractTemplateInputSchema>;

// ---------------------------------------------------------------------------
// Signatures — the evidence
// ---------------------------------------------------------------------------

export const contractSignatureSchema = z.object({
  id: uuidSchema,
  contractId: uuidSchema,
  party: signaturePartySchema,
  signerName: z.string(),
  signerEmail: z.string().nullable(),
  signerContactId: uuidSchema.nullable(),
  signerUserId: uuidSchema.nullable(),
  method: signatureMethodSchema,
  signatureText: z.string().nullable(),
  /** SHA-256 of the exact bytes signed. Re-checked on every download. */
  documentHash: z.string(),
  signedAt: timestampSchema,
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type ContractSignatureDto = z.infer<typeof contractSignatureSchema>;

export const contractEventSchema = z.object({
  id: uuidSchema,
  contractId: uuidSchema,
  type: contractEventTypeSchema,
  actorName: z.string().nullable(),
  contactId: uuidSchema.nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  occurredAt: timestampSchema,
});
export type ContractEventDto = z.infer<typeof contractEventSchema>;

// ---------------------------------------------------------------------------
// The contract
// ---------------------------------------------------------------------------

export const contractSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  clientId: uuidSchema,
  engagementId: uuidSchema.nullable(),
  proposalId: uuidSchema.nullable(),
  templateId: uuidSchema.nullable(),
  ownerId: uuidSchema.nullable(),
  title: z.string(),
  kind: contractKindSchema,
  status: contractStatusSchema,
  /** Rendered body. Frozen — and hashed — at send. */
  body: z.string(),
  bodyHash: z.string().nullable(),
  effectiveFrom: dateOnlySchema.nullable(),
  effectiveTo: dateOnlySchema.nullable(),
  autoRenew: z.boolean(),
  noticeDays: z.number().int(),
  renewalReminderDays: z.array(z.number().int()),
  valueMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  pdfFileId: uuidSchema.nullable(),
  publicUrl: z.string().nullable(),
  publicLinkExpiresAt: timestampSchema.nullable(),
  recipientContactId: uuidSchema.nullable(),
  recipientEmail: z.string().nullable(),
  sentAt: timestampSchema.nullable(),
  viewedAt: timestampSchema.nullable(),
  signedAt: timestampSchema.nullable(),
  countersignedAt: timestampSchema.nullable(),
  terminatedAt: timestampSchema.nullable(),
  terminationReason: z.string().nullable(),
  /** True when the document was signed on paper and uploaded (US-8.6). */
  externallySigned: z.boolean(),
  signatures: z.array(contractSignatureSchema),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ContractDto = z.infer<typeof contractSchema>;

export const contractSummarySchema = z.object({
  id: uuidSchema,
  number: z.string(),
  clientId: uuidSchema,
  engagementId: uuidSchema.nullable(),
  title: z.string(),
  kind: contractKindSchema,
  status: contractStatusSchema,
  effectiveFrom: dateOnlySchema.nullable(),
  effectiveTo: dateOnlySchema.nullable(),
  autoRenew: z.boolean(),
  valueMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  signedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type ContractSummaryDto = z.infer<typeof contractSummarySchema>;

const contractWritableShape = {
  title: z.string().trim().min(1).max(200),
  kind: contractKindSchema,
  body: z.string().max(200_000),
  engagementId: uuidSchema.nullable(),
  proposalId: uuidSchema.nullable(),
  ownerId: uuidSchema.nullable(),
  effectiveFrom: dateOnlySchema.nullable(),
  effectiveTo: dateOnlySchema.nullable(),
  autoRenew: z.boolean(),
  noticeDays: z.number().int().min(0).max(365),
  renewalReminderDays: z.array(z.number().int().min(1).max(365)).max(6),
  valueMinor: minorSchema.min(0).nullable(),
  currency: currencyCodeSchema,
  recipientContactId: uuidSchema.nullable(),
  recipientEmail: blankToNull(z.string().trim().toLowerCase().email().max(254)),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/contracts (perm `contracts:write`) → 201 Contract (draft).
 * With `templateId` the body is rendered from the template's merge fields;
 * `externallySigned: true` records a contract signed on paper.
 */
export const createContractInputSchema = z
  .object({
    clientId: uuidSchema,
    templateId: uuidSchema.optional(),
    externallySigned: z.boolean().optional(),
    ...contractWritableShape,
    title: contractWritableShape.title.optional(),
    kind: contractWritableShape.kind.optional(),
    body: contractWritableShape.body.optional(),
    engagementId: contractWritableShape.engagementId.optional(),
    proposalId: contractWritableShape.proposalId.optional(),
    ownerId: contractWritableShape.ownerId.optional(),
    effectiveFrom: contractWritableShape.effectiveFrom.optional(),
    effectiveTo: contractWritableShape.effectiveTo.optional(),
    autoRenew: contractWritableShape.autoRenew.optional(),
    noticeDays: contractWritableShape.noticeDays.optional(),
    renewalReminderDays: contractWritableShape.renewalReminderDays.optional(),
    valueMinor: contractWritableShape.valueMinor.optional(),
    currency: contractWritableShape.currency.optional(),
    recipientContactId: contractWritableShape.recipientContactId.optional(),
    recipientEmail: contractWritableShape.recipientEmail.optional(),
    tags: contractWritableShape.tags.optional(),
    customFields: contractWritableShape.customFields.optional(),
  })
  .strict();
export type CreateContractInput = z.input<typeof createContractInputSchema>;
export type CreateContractData = z.output<typeof createContractInputSchema>;

/** PATCH /api/v1/contracts/:id (perm `contracts:write`) → Contract. DRAFT ONLY — a sent contract is the record (409). */
export const updateContractInputSchema = z.object(contractWritableShape).partial().strict();
export type UpdateContractInput = z.input<typeof updateContractInputSchema>;
export type UpdateContractData = z.output<typeof updateContractInputSchema>;

export const listContractsQuerySchema = listQuerySchema.extend({
  status: contractStatusSchema.optional(),
  clientId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  kind: contractKindSchema.optional(),
  /** Contracts whose end date falls within the next N days. */
  expiringInDays: z.coerce.number().int().min(1).max(365).optional(),
  sort: z.enum(["createdAt", "number", "effectiveTo", "valueMinor"]).optional(),
});
export type ListContractsQuery = z.infer<typeof listContractsQuerySchema>;

/** POST /api/v1/contracts/:id/send (perm `contracts:write`) → Contract, status `sent`, body frozen and hashed. */
export const sendContractInputSchema = z
  .object({
    to: z.string().trim().toLowerCase().email().max(254).optional(),
    message: z.string().trim().max(4000).optional(),
    linkDays: z.number().int().min(1).max(365).optional(),
  })
  .strict();
export type SendContractInput = z.input<typeof sendContractInputSchema>;
export type SendContractData = z.output<typeof sendContractInputSchema>;

/** POST /api/v1/contracts/:id/countersign (perm `contracts:sign`) → Contract. The client must have signed first (409). */
export const countersignContractInputSchema = z
  .object({
    signerName: z.string().trim().min(1).max(160).optional(),
    method: signatureMethodSchema.optional(),
    signatureText: z.string().trim().max(160).optional(),
  })
  .strict();
export type CountersignContractInput = z.input<typeof countersignContractInputSchema>;
export type CountersignContractData = z.output<typeof countersignContractInputSchema>;

/** POST /api/v1/contracts/:id/terminate (perm `contracts:write`) → Contract, status `terminated`. */
export const terminateContractInputSchema = z
  .object({ reason: z.string().trim().min(3).max(1000), effectiveOn: dateOnlySchema.optional() })
  .strict();
export type TerminateContractInput = z.input<typeof terminateContractInputSchema>;
export type TerminateContractData = z.output<typeof terminateContractInputSchema>;

/** GET /api/v1/contracts/:id/pdf (perm `contracts:read`) → the paper's state and a signed download link. */
export const contractPdfSchema = z.object({
  contractId: uuidSchema,
  status: z.enum(["missing", "queued", "rendered", "failed"]),
  fileId: uuidSchema.nullable(),
  filename: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  /** False when the body changed after the PDF was made — the download re-renders. */
  hashMatches: z.boolean(),
  url: z.string().nullable(),
  urlExpiresAt: timestampSchema.nullable(),
  renderedAt: timestampSchema.nullable(),
  lastError: z.string().nullable(),
});
export type ContractPdfDto = z.infer<typeof contractPdfSchema>;

// ---------------------------------------------------------------------------
// The public (login-free) signing surface
// ---------------------------------------------------------------------------

export const publicContractTokenParamsSchema = z.object({ token: z.string().min(40).max(200) });
export type PublicContractTokenParams = z.infer<typeof publicContractTokenParamsSchema>;

export const publicContractSignatureSchema = z.object({
  party: signaturePartySchema,
  signerName: z.string(),
  signedAt: timestampSchema,
  method: signatureMethodSchema,
});

export const publicContractViewSchema = z.object({
  number: z.string(),
  title: z.string(),
  kind: contractKindSchema,
  status: contractStatusSchema,
  agencyName: z.string(),
  agencyLogoUrl: z.string().nullable(),
  brandColor: z.string().nullable(),
  clientName: z.string(),
  body: z.string(),
  documentHash: z.string(),
  effectiveFrom: dateOnlySchema.nullable(),
  effectiveTo: dateOnlySchema.nullable(),
  valueMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  signatures: z.array(publicContractSignatureSchema),
  signable: z.boolean(),
  pdfUrl: z.string().nullable(),
});
export type PublicContractViewDto = z.infer<typeof publicContractViewSchema>;

/**
 * POST /api/public/v1/contracts/:token/sign — the client signs.
 * `documentHash` is what the browser was shown; a mismatch answers 409 so
 * nobody can sign a version they never saw.
 */
export const publicSignContractInputSchema = z
  .object({
    signerName: z.string().trim().min(1).max(160),
    signerEmail: z.string().trim().toLowerCase().email().max(254),
    method: signatureMethodSchema.optional(),
    signatureText: z.string().trim().max(160).optional(),
    /** data:image/png;base64,… for a drawn signature. */
    signatureImage: z.string().max(400_000).optional(),
    documentHash: z.string().length(64).optional(),
    acceptedTerms: z.literal(true),
  })
  .strict();
export type PublicSignContractInput = z.input<typeof publicSignContractInputSchema>;
export type PublicSignContractData = z.output<typeof publicSignContractInputSchema>;
